import { CopyButton } from "@components/CopyButton";
import { EmailLink } from "@components/EmailLink";
import { invenioConfig } from "@config";
import { BackOfficeRoutes } from "@routes/backoffice/backofficeUrls";
import PropTypes from "prop-types";
import React from "react";
import { Link } from "react-router-dom";
import { Card, Icon, Button } from "semantic-ui-react";

const PatronCard = ({ patron, profileImages }) => {
  // Problematic user IDs to filter out
  const problematicUserIds = ["2", "7", "12", "13", "14", "15", "41"];

  const name = patron.metadata.name;
  const email = patron.metadata.email;
  const patronId = patron.metadata.profile_id;
  const userId = patron.metadata.id;

  // Check if this is a problematic user
  const isProblematicUser = problematicUserIds.includes(userId);

  // Get profile image from batch data using user_id
  const imageInfo = profileImages[userId] || {
    image_url: null,
    has_image: false,
  };
  const profileImageUrl = imageInfo.image_url;

  // Role-based color code using database user types
  const getCardColor = (patron) => {
    const userType = patron.metadata.user_type;
    const patronType = patron.metadata.patron_type;
    const role = patron.metadata.role;

    let normalizedType = "student";

    if (userType) {
      normalizedType = userType.trim().toLowerCase();
    } else if (role) {
      normalizedType = role.trim().toLowerCase();
    }
    // 3. Fallback to patron_type field (third priority)
    else if (patronType) {
      normalizedType = patronType.trim().toLowerCase();
    }

    switch (normalizedType) {
      case "admin":
        return {
          color: "red",
          borderBottom: "3px solid #db2828",
        };
      case "librarian":
        return {
          color: "teal",
          borderBottom: "3px solid #00b5ad",
        };
      case "faculty":
        return {
          color: "purple",
          borderBottom: "3px solid #a333c8",
        };
      case "researcher":
        return {
          color: "orange",
          borderBottom: "3px solid #f2711c",
        };
      case "visitor":
        return {
          color: "brown",
          borderBottom: "3px solid #a5673f",
        };
      case "student":
      default:
        return {
          color: "blue",
          borderBottom: "3px solid #2185d0",
        };
    }
  };

  const cardStyle = getCardColor(patron);

  // Email body for the EmailLink
  const emailBody = `${name},${invenioConfig.APP.EMAILS_PREFILL.footer}`;

  // Format name to show "Last, First" format
  const formatName = (name) => {
    if (!name) return "No Name";
    const nameParts = name.trim().split(" ");

    if (nameParts.length === 1) {
      return nameParts[0];
    }

    const lastName = nameParts[nameParts.length - 1];
    const firstAndMiddle = nameParts.slice(0, -1).join(" ");
    return `${lastName}, ${firstAndMiddle}`;
  };

  // Generate initials for fallback
  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((part) => part.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("");
  };

  // Handle image load error
  const handleImageError = (e) => {
    e.target.style.display = "none";
    e.target.nextSibling.style.display = "flex";
  };

  // Return null to prevent rendering problematic users
  if (isProblematicUser) {
    return null;
  }

  return (
    <Card
      color={cardStyle.color}
      fluid
      style={{
        borderBottom: cardStyle.borderBottom,
      }}
    >
      <Card.Content style={{ padding: "0" }}>
        <div style={{ display: "flex", minHeight: "120px" }}>
          {/* Left side - Profile Picture */}
          <div
            style={{
              width: "96px",
              height: "96px",
              backgroundColor: "#f8f9fa",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRight: "1px solid #e1e1e1",
              margin: "12px",
              borderRadius: "8px",
              overflow: "hidden",
              position: "relative",
            }}
          >
            {profileImageUrl ? (
              <>
                <img
                  src={profileImageUrl}
                  alt={`${formatName(name)} profile picture`}
                  style={{
                    width: "96px",
                    height: "96px",
                    objectFit: "cover",
                    borderRadius: "8px",
                    border: "2px solid rgb(145, 141, 135)",
                  }}
                  onError={handleImageError}
                />
                <div
                  style={{
                    width: "96px",
                    height: "96px",
                    backgroundColor: "#e9ecef",
                    borderRadius: "8px",
                    display: "none",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: "#6c757d",
                    position: "absolute",
                    top: 0,
                    left: 0,
                    border: "2px solid rgb(145, 141, 135)",
                  }}
                >
                  {name ? getInitials(name) : <Icon name="user" size="large" />}
                </div>
              </>
            ) : (
              <div
                style={{
                  width: "96px",
                  height: "96px",
                  backgroundColor: "#e9ecef",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                  fontWeight: "bold",
                  color: "#6c757d",
                  border: "2px solid rgb(145, 141, 135)",
                }}
              >
                {name ? getInitials(name) : <Icon name="user" size="large" />}
              </div>
            )}
          </div>

          {/* Right side - Patron Details */}
          <div style={{ flex: 1, padding: "14px" }}>
            <Card.Header style={{ marginBottom: "8px" }}>
              <Link
                to={BackOfficeRoutes.patronDetailsFor(userId)}
                data-test={patron.metadata.pid}
                style={{ color: "inherit", textDecoration: "none" }}
              >
                {formatName(name)}
              </Link>
            </Card.Header>

            <Card.Meta style={{ marginBottom: "12px" }}>
              <span>Patron ID: {patronId || "Not assigned"}</span>
              <br />
              <small style={{ color: "#999" }}>User ID: {userId}</small>
            </Card.Meta>

            <Card.Description>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Icon name="mail" style={{ marginRight: "4px" }} />
                <span style={{ marginRight: "8px" }}>
                  <EmailLink
                    email={email}
                    subject={invenioConfig.APP.EMAILS_PREFILL.subjectPrefix}
                    body={emailBody}
                  />
                </span>
                <CopyButton text={email} />
              </div>
            </Card.Description>
          </div>
        </div>
      </Card.Content>

      <Card.Content extra>
        <Button
          as={Link}
          to={BackOfficeRoutes.patronDetailsFor(userId)}
          basic
          color="green"
          fluid
          data-test={patron.metadata.pid}
        >
          <Icon name="eye" />
          View Details
        </Button>
      </Card.Content>
    </Card>
  );
};

PatronCard.propTypes = {
  patron: PropTypes.object.isRequired,
  profileImages: PropTypes.object.isRequired,
};

export default PatronCard;
